import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Users,
  DollarSign,
  Calendar,
  CheckCircle,
  TrendingUp,
  Plus,
  Edit,
  Trash2,
  LogOut,
  Clock,
} from 'lucide-react';

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isWaitlistDialogOpen, setIsWaitlistDialogOpen] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [editingWaitlistEntry, setEditingWaitlistEntry] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    age: 'Adult',
    email: '',
    assessment_type: 'ASD',
    intake_paperwork: 'No',
    mhcp: 'No',
    payment_type: 'Private',
    total_cost: '',
    total_paid: '',
    process: 'Intake Sent',
    process_date: '',
    date_of_next_session: '',
    notes: '',
  });
  const [dateNotSet, setDateNotSet] = useState(false);
  const [waitlistFormData, setWaitlistFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: 'ASD Assessment',
    notes: '',
  });

  // Check auth on mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      toast.error('Please login first');
      navigate('/admin-login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      // Fetch metrics, assessments, and waitlist in parallel
      const [metricsRes, assessmentsRes, waitlistRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard/metrics`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/assessments`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/waitlist`, { headers }),
      ]);

      if (metricsRes.ok) {
        const metricsData = await metricsRes.json();
        setMetrics(metricsData);
      }

      if (assessmentsRes.ok) {
        const assessmentsData = await assessmentsRes.json();
        setAssessments(assessmentsData);
      }

      if (waitlistRes.ok) {
        const waitlistData = await waitlistRes.json();
        setWaitlist(waitlistData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    toast.success('Logged out successfully');
    navigate('/admin-login');
  };

  const handleOpenDialog = (assessment = null) => {
    if (assessment) {
      // Edit mode
      setEditingAssessment(assessment);
      const hasDate = assessment.date_of_next_session && assessment.date_of_next_session !== null;
      setDateNotSet(!hasDate);
      setFormData({
        name: assessment.name,
        age: assessment.age,
        email: assessment.email,
        assessment_type: assessment.assessment_type,
        intake_paperwork: assessment.intake_paperwork,
        mhcp: assessment.mhcp || 'No',
        payment_type: assessment.payment_type,
        total_cost: assessment.total_cost.toString(),
        total_paid: assessment.total_paid.toString(),
        process: assessment.process,
        process_date: assessment.process_date ? assessment.process_date.split('T')[0] : '',
        date_of_next_session: hasDate ? assessment.date_of_next_session.split('T')[0] : '',
        notes: assessment.notes || '',
      });
    } else {
      // Add mode
      setEditingAssessment(null);
      setDateNotSet(false);
      setFormData({
        name: '',
        age: 'Adult',
        email: '',
        assessment_type: 'ASD',
        intake_paperwork: 'No',
        payment_type: 'Private',
        total_cost: '',
        total_paid: '',
        process: 'Intake Sent',
        process_date: '',
        date_of_next_session: '',
        notes: '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const token = localStorage.getItem('adminToken');

    try {
      const payload = {
        ...formData,
        total_cost: parseFloat(formData.total_cost) || 0,
        total_paid: parseFloat(formData.total_paid) || 0,
        process_date: formData.process_date ? `${formData.process_date}T00:00:00Z` : null,
        date_of_next_session: dateNotSet ? null : (formData.date_of_next_session ? `${formData.date_of_next_session}T00:00:00Z` : null),
        notes: formData.notes || null,
      };

      const url = editingAssessment
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/assessments/${editingAssessment.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/assessments`;

      const method = editingAssessment ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingAssessment ? 'Assessment updated!' : 'Assessment created!');
        setIsDialogOpen(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to save assessment', {
          description: error.detail || 'Please try again',
        });
      }
    } catch (error) {
      console.error('Error saving assessment:', error);
      toast.error('Failed to save assessment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this assessment?')) {
      return;
    }

    const token = localStorage.getItem('adminToken');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/assessments/${id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Assessment deleted');
        fetchData();
      } else {
        toast.error('Failed to delete assessment');
      }
    } catch (error) {
      console.error('Error deleting assessment:', error);
      toast.error('Failed to delete assessment');
    }
  };

  // Waitlist handlers
  const handleOpenWaitlistDialog = (entry = null) => {
    if (entry) {
      setEditingWaitlistEntry(entry);
      setWaitlistFormData({
        name: entry.name,
        email: entry.email,
        phone: entry.phone || '',
        interest: entry.interest,
        notes: entry.notes || '',
      });
    } else {
      setEditingWaitlistEntry(null);
      setWaitlistFormData({
        name: '',
        email: '',
        phone: '',
        interest: 'ASD Assessment',
        notes: '',
      });
    }
    setIsWaitlistDialogOpen(true);
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const token = localStorage.getItem('adminToken');

    try {
      const url = editingWaitlistEntry
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/waitlist/${editingWaitlistEntry.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/waitlist`;

      const method = editingWaitlistEntry ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(waitlistFormData),
      });

      if (response.ok) {
        toast.success(editingWaitlistEntry ? 'Waitlist entry updated!' : 'Added to waitlist!');
        setIsWaitlistDialogOpen(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to save entry', {
          description: error.detail || 'Please try again',
        });
      }
    } catch (error) {
      console.error('Error saving waitlist entry:', error);
      toast.error('Failed to save entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteWaitlist = async (id) => {
    if (!window.confirm('Are you sure you want to remove this person from the waitlist?')) {
      return;
    }

    const token = localStorage.getItem('adminToken');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/waitlist/${id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Removed from waitlist');
        fetchData();
      } else {
        toast.error('Failed to remove entry');
      }
    } catch (error) {
      console.error('Error deleting waitlist entry:', error);
      toast.error('Failed to remove entry');
    }
  };

  const getStatusBadgeColor = (process) => {
    const colors = {
      'Intake Sent': 'bg-blue-100 text-blue-800 hover:bg-blue-200',
      'Intake Received': 'bg-purple-100 text-purple-800 hover:bg-purple-200',
      'Background Booked': 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200',
      'In Progress': 'bg-amber-100 text-amber-800 hover:bg-amber-200',
      'Report Writing': 'bg-orange-100 text-orange-800 hover:bg-orange-200',
      'Report Completed': 'bg-teal-100 text-teal-800 hover:bg-teal-200',
      'Feedback Session': 'bg-cyan-100 text-cyan-800 hover:bg-cyan-200',
      'Completed': 'bg-[#A8B9A0] text-[#3A3530] hover:bg-[#8B9D83]',
      'Disengaged': 'bg-gray-100 text-gray-800 hover:bg-gray-200',
    };
    return colors[process] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(amount);
  };

  // Separate assessments into three categories
  const activeAssessments = assessments.filter(
    a => a.completed !== 'Disengaged' && a.completed !== 'Completed'
  );
  const completedAssessments = assessments.filter(a => a.completed === 'Completed');
  const disengagedAssessments = assessments.filter(a => a.completed === 'Disengaged');

  const getPaymentStatusIcon = (assessment) => {
    const status = assessment.payment_status;
    const balance = assessment.balance_owing;
    
    switch (status) {
      case 'Paid in Full':
        return (
          <div className="flex items-center justify-center" title="Paid in Full">
            <CheckCircle className="h-5 w-5 text-[#7BA05B]" />
          </div>
        );
      case 'Partial Payment':
        return (
          <div className="flex flex-col items-center" title={`Partial Payment - ${formatCurrency(balance)} owing`}>
            <span className="text-[#D4A574] text-xl">⚠</span>
            <span className="text-xs font-semibold text-[#D4A574] whitespace-nowrap">
              {formatCurrency(balance)}
            </span>
          </div>
        );
      case 'Unpaid':
        return (
          <div className="flex items-center justify-center" title="Unpaid">
            <span className="text-[#C17B6B] text-xl font-bold">✗</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center justify-center" title="No Quote">
            <span className="text-gray-400">-</span>
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#8B9D83] border-r-transparent"></div>
          <p className="mt-4 text-[#6B6560]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Dashboard | Navigating Minds Psychology</title>
        {/* Prevent search engines from indexing admin pages */}
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-[#FDFBF7] py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl lg:text-4xl font-semibold text-[#3A3530] font-heading">
                Assessment Dashboard
              </h1>
              <p className="mt-2 text-[#6B6560]">
                Welcome back, {localStorage.getItem('adminEmail')}
              </p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              data-testid="logout-button"
              className="border-2 border-[#8B9D83] text-[#6B7D63] hover:bg-[#8B9D83] hover:text-white"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>

          {/* Tabs for Assessments and Waitlist */}
          <Tabs defaultValue="assessments" className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2 mb-8">
              <TabsTrigger value="assessments" className="data-[state=active]:bg-[#8B9D83] data-[state=active]:text-white">
                Assessments
              </TabsTrigger>
              <TabsTrigger value="waitlist" className="data-[state=active]:bg-[#8B9D83] data-[state=active]:text-white">
                Waitlist ({waitlist.length})
              </TabsTrigger>
            </TabsList>

            {/* Assessments Tab */}
            <TabsContent value="assessments">
          {/* Metrics Cards */}
          {metrics && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-6 mb-8">
              <Card data-testid="metric-card-active" className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-[#6B6560] flex items-center">
                    <Users className="mr-2 h-4 w-4 text-[#8B9D83]" />
                    Active Assessments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#3A3530]">{metrics.total_active}</p>
                </CardContent>
              </Card>

              <Card data-testid="metric-card-payments" className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-[#6B6560] flex items-center">
                    <DollarSign className="mr-2 h-4 w-4 text-[#D4A574]" />
                    Outstanding
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#3A3530]">
                    {formatCurrency(metrics.outstanding_payments)}
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="metric-card-unpaid-count" className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-[#6B6560] flex items-center">
                    <DollarSign className="mr-2 h-4 w-4 text-[#C17B6B]" />
                    To Be Paid
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#3A3530]">{metrics.unpaid_count}</p>
                  <p className="text-xs text-[#9B9590] mt-1">clients</p>
                </CardContent>
              </Card>

              <Card data-testid="metric-card-due" className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-[#6B6560] flex items-center">
                    <Calendar className="mr-2 h-4 w-4 text-[#8B9D83]" />
                    Due This Week
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#3A3530]">{metrics.due_this_week}</p>
                </CardContent>
              </Card>

              <Card data-testid="metric-card-completed-month" className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-[#6B6560] flex items-center">
                    <CheckCircle className="mr-2 h-4 w-4 text-[#7BA05B]" />
                    This Month
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#3A3530]">{metrics.completed_this_month}</p>
                </CardContent>
              </Card>

              <Card data-testid="metric-card-completed-total" className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-[#6B6560] flex items-center">
                    <CheckCircle className="mr-2 h-4 w-4 text-[#7BA05B]" />
                    Total Completed
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#3A3530]">{metrics.completed_all_time}</p>
                </CardContent>
              </Card>

              <Card data-testid="metric-card-stages" className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-[#6B6560] flex items-center">
                    <TrendingUp className="mr-2 h-4 w-4 text-[#8B9D83]" />
                    By Stage
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 text-xs">
                    {Object.entries(metrics.by_stage).slice(0, 3).map(([stage, count]) => (
                      <div key={stage} className="flex justify-between">
                        <span className="text-[#6B6560] truncate">{stage}</span>
                        <span className="font-semibold text-[#3A3530]">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Actions Bar */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-[#3A3530] font-heading">
              Active Assessments ({activeAssessments.length})
            </h2>
            <Button
              onClick={() => handleOpenDialog()}
              data-testid="add-assessment-button"
              className="bg-[#8B9D83] hover:bg-[#6B7D63] text-white rounded-full"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Client
            </Button>
          </div>

          {/* Active Assessments Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-[#E8E4DC] overflow-hidden mb-12">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="assessments-table">
                <thead className="bg-[#F5F1E8] border-b border-[#E8E4DC]">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Age</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Assessment</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-[#3A3530]">MHCP</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Process</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Next Session</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Total</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-[#3A3530]">Paid?</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Next Step</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-[#3A3530]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8E4DC]">
                  {activeAssessments.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="px-6 py-12 text-center text-[#9B9590]">
                        No active assessments. Click "Add Client" to get started.
                      </td>
                    </tr>
                  ) : (
                    activeAssessments.map((assessment) => (
                      <tr key={assessment.id} className="hover:bg-[#FDFBF7] transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-[#3A3530]">
                          {assessment.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#6B6560]">{assessment.age}</td>
                        <td className="px-6 py-4 text-sm text-[#6B6560]">
                          {assessment.assessment_type}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Badge data-testid={`mhcp-badge-${assessment.id}`} className={`rounded-full px-3 py-1 ${assessment.mhcp === 'Yes' ? 'bg-[#8B9D83]/20 text-[#5A6F52] border border-[#8B9D83]/30' : 'bg-[#E8E4DC]/50 text-[#9B9590] border border-[#E8E4DC]'}`}>
                            {assessment.mhcp || 'No'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <Badge className={`${getStatusBadgeColor(assessment.process)} rounded-full px-3 py-1`}>
                            {assessment.process}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#6B6560]">
                          {formatDate(assessment.date_of_next_session)}
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#3A3530]">
                          {formatCurrency(assessment.total_cost)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {getPaymentStatusIcon(assessment)}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#6B6560]">{assessment.next_step}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              onClick={() => handleOpenDialog(assessment)}
                              data-testid={`edit-assessment-${assessment.id}`}
                              variant="ghost"
                              size="sm"
                              className="text-[#8B9D83] hover:bg-[#F5F1E8]"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => handleDelete(assessment.id)}
                              data-testid={`delete-assessment-${assessment.id}`}
                              variant="ghost"
                              size="sm"
                              className="text-[#C17B6B] hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>


          {/* Completed Assessments Section */}
          {completedAssessments.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-6 mt-12">
                <h2 className="text-2xl font-semibold text-[#7BA05B] font-heading">
                  Completed Assessments ({completedAssessments.length})
                </h2>
              </div>

              <div className="bg-green-50/50 rounded-2xl shadow-sm border border-green-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="completed-table">
                    <thead className="bg-green-100/50 border-b border-green-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Name</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Age</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Assessment</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Total</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold text-[#3A3530]">Paid?</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Notes</th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-[#3A3530]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-green-200">
                      {completedAssessments.map((assessment) => (
                        <tr key={assessment.id} className="hover:bg-green-100/30 transition-colors">
                          <td className="px-6 py-4 text-sm font-medium text-[#3A3530]">
                            {assessment.name}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#6B6560]">{assessment.age}</td>
                          <td className="px-6 py-4 text-sm text-[#6B6560]">
                            {assessment.assessment_type}
                          </td>
                          <td className="px-6 py-4 text-sm font-semibold text-[#3A3530]">
                            {formatCurrency(assessment.total_cost)}
                          </td>
                          <td className="px-6 py-4 text-center">
                            {getPaymentStatusIcon(assessment)}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#6B6560]">
                            {assessment.notes || 'No notes'}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                onClick={() => handleOpenDialog(assessment)}
                                data-testid={`edit-completed-${assessment.id}`}
                                variant="ghost"
                                size="sm"
                                className="text-[#8B9D83] hover:bg-green-100"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                onClick={() => handleDelete(assessment.id)}
                                data-testid={`delete-completed-${assessment.id}`}
                                variant="ghost"
                                size="sm"
                                className="text-[#C17B6B] hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Disengaged Clients Section */}
          {disengagedAssessments.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-6 mt-12">
                <h2 className="text-2xl font-semibold text-[#6B6560] font-heading">
                  Disengaged Clients ({disengagedAssessments.length})
                </h2>
              </div>

              <div className="bg-gray-50 rounded-2xl shadow-sm border border-gray-200 overflow-hidden opacity-75">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="disengaged-table">
                    <thead className="bg-gray-100 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Name</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Age</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Assessment</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Notes</th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {disengagedAssessments.map((assessment) => (
                        <tr key={assessment.id} className="hover:bg-gray-100 transition-colors">
                          <td className="px-6 py-4 text-sm font-medium text-gray-700">
                            {assessment.name}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">{assessment.age}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {assessment.assessment_type}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {assessment.notes || 'No notes'}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                onClick={() => handleOpenDialog(assessment)}
                                data-testid={`edit-disengaged-${assessment.id}`}
                                variant="ghost"
                                size="sm"
                                className="text-gray-600 hover:bg-gray-200"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                onClick={() => handleDelete(assessment.id)}
                                data-testid={`delete-disengaged-${assessment.id}`}
                                variant="ghost"
                                size="sm"
                                className="text-[#C17B6B] hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
            </TabsContent>

            {/* Waitlist Tab */}
            <TabsContent value="waitlist">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-[#3A3530] font-heading">
                  Waitlist ({waitlist.length})
                </h2>
                <Button
                  onClick={() => handleOpenWaitlistDialog()}
                  data-testid="add-waitlist-button"
                  className="bg-[#8B9D83] hover:bg-[#6B7D63] text-white rounded-full"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add to Waitlist
                </Button>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-[#E8E4DC] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="waitlist-table">
                    <thead className="bg-[#F5F1E8] border-b border-[#E8E4DC]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Name</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Email</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Phone</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Interest</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Added</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Notes</th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-[#3A3530]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8E4DC]">
                      {waitlist.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-6 py-12 text-center text-[#9B9590]">
                            No entries in waitlist. Click "Add to Waitlist" to get started.
                          </td>
                        </tr>
                      ) : (
                        waitlist.map((entry) => (
                          <tr key={entry.id} className="hover:bg-[#FDFBF7] transition-colors">
                            <td className="px-6 py-4 text-sm font-medium text-[#3A3530]">
                              {entry.name}
                            </td>
                            <td className="px-6 py-4 text-sm text-[#6B6560]">{entry.email}</td>
                            <td className="px-6 py-4 text-sm text-[#6B6560]">{entry.phone || '-'}</td>
                            <td className="px-6 py-4 text-sm text-[#6B6560]">{entry.interest}</td>
                            <td className="px-6 py-4 text-sm text-[#6B6560]">
                              {new Date(entry.added_at).toLocaleDateString('en-AU', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </td>
                            <td className="px-6 py-4 text-sm text-[#6B6560]">
                              {entry.notes || '-'}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end space-x-2">
                                <Button
                                  onClick={() => handleOpenWaitlistDialog(entry)}
                                  data-testid={`edit-waitlist-${entry.id}`}
                                  variant="ghost"
                                  size="sm"
                                  className="text-[#8B9D83] hover:bg-[#F5F1E8]"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  onClick={() => handleDeleteWaitlist(entry.id)}
                                  data-testid={`delete-waitlist-${entry.id}`}
                                  variant="ghost"
                                  size="sm"
                                  className="text-[#C17B6B] hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Add/Edit Assessment Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-[#3A3530] font-heading">
              {editingAssessment ? 'Edit Assessment' : 'Add New Client'}
            </DialogTitle>
            <DialogDescription className="text-[#6B6560]">
              {editingAssessment
                ? 'Update the client assessment details below'
                : 'Enter the new client assessment details below'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  data-testid="form-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="border-[#E8E4DC] focus:border-[#8B9D83]"
                />
              </div>

              {/* Age */}
              <div className="space-y-2">
                <Label htmlFor="age">Age *</Label>
                <Select
                  value={formData.age}
                  onValueChange={(value) => setFormData({ ...formData, age: value })}
                >
                  <SelectTrigger data-testid="form-age-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Minor">Minor</SelectItem>
                    <SelectItem value="Adult">Adult</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="form-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="border-[#E8E4DC] focus:border-[#8B9D83]"
                />
              </div>

              {/* Assessment Type */}
              <div className="space-y-2">
                <Label htmlFor="assessment_type">Assessment Type *</Label>
                <Select
                  value={formData.assessment_type}
                  onValueChange={(value) => setFormData({ ...formData, assessment_type: value })}
                >
                  <SelectTrigger data-testid="form-assessment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ASD">ASD</SelectItem>
                    <SelectItem value="ADHD">ADHD</SelectItem>
                    <SelectItem value="Combination (ASD + ADHD)">Combination (ASD + ADHD)</SelectItem>
                    <SelectItem value="ASD & Dyslexia">ASD & Dyslexia</SelectItem>
                    <SelectItem value="ADHD & Dyslexia">ADHD & Dyslexia</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Intake Paperwork */}
              <div className="space-y-2">
                <Label htmlFor="intake_paperwork">Intake Paperwork</Label>
                <Select
                  value={formData.intake_paperwork}
                  onValueChange={(value) => setFormData({ ...formData, intake_paperwork: value })}
                >
                  <SelectTrigger data-testid="form-intake-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* MHCP */}
              <div className="space-y-2">
                <Label htmlFor="mhcp">MHCP</Label>
                <Select
                  value={formData.mhcp}
                  onValueChange={(value) => setFormData({ ...formData, mhcp: value })}
                >
                  <SelectTrigger data-testid="form-mhcp-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Payment Type */}
              <div className="space-y-2">
                <Label htmlFor="payment_type">Payment Type *</Label>
                <Select
                  value={formData.payment_type}
                  onValueChange={(value) => setFormData({ ...formData, payment_type: value })}
                >
                  <SelectTrigger data-testid="form-payment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NDIS">NDIS</SelectItem>
                    <SelectItem value="Private">Private</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Total Cost */}
              <div className="space-y-2">
                <Label htmlFor="total_cost">Total Cost (AUD) *</Label>
                <Input
                  id="total_cost"
                  type="number"
                  step="0.01"
                  data-testid="form-total-cost-input"
                  value={formData.total_cost}
                  onChange={(e) => setFormData({ ...formData, total_cost: e.target.value })}
                  required
                  className="border-[#E8E4DC] focus:border-[#8B9D83]"
                />
              </div>

              {/* Total Paid */}
              <div className="space-y-2">
                <Label htmlFor="total_paid">Total Paid (AUD)</Label>
                <Input
                  id="total_paid"
                  type="number"
                  step="0.01"
                  data-testid="form-total-paid-input"
                  value={formData.total_paid}
                  onChange={(e) => setFormData({ ...formData, total_paid: e.target.value })}
                  className="border-[#E8E4DC] focus:border-[#8B9D83]"
                />
              </div>

              {/* Process */}
              <div className="space-y-2">
                <Label htmlFor="process">Process Stage *</Label>
                <Select
                  value={formData.process}
                  onValueChange={(value) => setFormData({ ...formData, process: value })}
                >
                  <SelectTrigger data-testid="form-process-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Intake Sent">Intake Sent</SelectItem>
                    <SelectItem value="Intake Received">Intake Received</SelectItem>
                    <SelectItem value="Background Booked">Background Booked</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Report Writing">Report Writing</SelectItem>
                    <SelectItem value="Report Completed">Report Completed</SelectItem>
                    <SelectItem value="Feedback Session">Feedback Session</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Disengaged">Disengaged</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date of Next Session */}
              <div className="space-y-2">
                <Label htmlFor="date_of_next_session">Date of Next Session</Label>
                <div className="space-y-3">
                  <Input
                    id="date_of_next_session"
                    type="date"
                    data-testid="form-next-session-input"
                    value={dateNotSet ? '' : (formData.date_of_next_session || '')}
                    onChange={(e) => {
                      setFormData({ ...formData, date_of_next_session: e.target.value });
                      // Uncheck "date not set" if user enters a date
                      if (e.target.value && dateNotSet) {
                        setDateNotSet(false);
                      }
                    }}
                    disabled={dateNotSet}
                    className="border-[#E8E4DC] focus:border-[#8B9D83] disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                  />
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="date_not_set"
                      data-testid="form-date-not-set-checkbox"
                      checked={dateNotSet}
                      onCheckedChange={(checked) => {
                        setDateNotSet(checked);
                        if (checked) {
                          // Clear the date field when checkbox is checked
                          setFormData({ ...formData, date_of_next_session: '' });
                        }
                      }}
                      className="border-[#8B9D83] data-[state=checked]:bg-[#8B9D83]"
                    />
                    <Label
                      htmlFor="date_not_set"
                      className="text-sm text-[#6B6560] cursor-pointer font-normal"
                    >
                      Date not set (will appear at bottom of list)
                    </Label>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                data-testid="form-notes-textarea"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={4}
                className="border-[#E8E4DC] focus:border-[#8B9D83] resize-none"
                placeholder="Add any additional notes or observations..."
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="border-[#E8E4DC]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="form-submit-button"
                disabled={isSubmitting}
                className="bg-[#8B9D83] hover:bg-[#6B7D63] text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : (editingAssessment ? 'Update Assessment' : 'Create Assessment')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Waitlist Dialog */}
      <Dialog open={isWaitlistDialogOpen} onOpenChange={setIsWaitlistDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-[#3A3530] font-heading">
              {editingWaitlistEntry ? 'Edit Waitlist Entry' : 'Add to Waitlist'}
            </DialogTitle>
            <DialogDescription className="text-[#6B6560]">
              {editingWaitlistEntry
                ? 'Update the waitlist entry details'
                : 'Add a new person to the waitlist'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleWaitlistSubmit} className="space-y-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="waitlist-name">Name *</Label>
              <Input
                id="waitlist-name"
                data-testid="waitlist-form-name-input"
                value={waitlistFormData.name}
                onChange={(e) => setWaitlistFormData({ ...waitlistFormData, name: e.target.value })}
                required
                className="border-[#E8E4DC] focus:border-[#8B9D83]"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="waitlist-email">Email *</Label>
              <Input
                id="waitlist-email"
                type="email"
                data-testid="waitlist-form-email-input"
                value={waitlistFormData.email}
                onChange={(e) => setWaitlistFormData({ ...waitlistFormData, email: e.target.value })}
                required
                className="border-[#E8E4DC] focus:border-[#8B9D83]"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="waitlist-phone">Phone (optional)</Label>
              <Input
                id="waitlist-phone"
                type="tel"
                data-testid="waitlist-form-phone-input"
                value={waitlistFormData.phone}
                onChange={(e) => setWaitlistFormData({ ...waitlistFormData, phone: e.target.value })}
                className="border-[#E8E4DC] focus:border-[#8B9D83]"
              />
            </div>

            {/* Interest */}
            <div className="space-y-2">
              <Label htmlFor="waitlist-interest">Interest *</Label>
              <Select
                value={waitlistFormData.interest}
                onValueChange={(value) => setWaitlistFormData({ ...waitlistFormData, interest: value })}
              >
                <SelectTrigger data-testid="waitlist-form-interest-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ASD Assessment">ASD Assessment</SelectItem>
                  <SelectItem value="ADHD Assessment">ADHD Assessment</SelectItem>
                  <SelectItem value="Combination Assessment">Combination Assessment</SelectItem>
                  <SelectItem value="Therapy Services">Therapy Services</SelectItem>
                  <SelectItem value="General Enquiry">General Enquiry</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="waitlist-notes">Notes</Label>
              <Textarea
                id="waitlist-notes"
                data-testid="waitlist-form-notes-textarea"
                value={waitlistFormData.notes}
                onChange={(e) => setWaitlistFormData({ ...waitlistFormData, notes: e.target.value })}
                rows={3}
                className="border-[#E8E4DC] focus:border-[#8B9D83] resize-none"
                placeholder="Add any notes about this lead..."
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsWaitlistDialogOpen(false)}
                className="border-[#E8E4DC]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="waitlist-form-submit-button"
                disabled={isSubmitting}
                className="bg-[#8B9D83] hover:bg-[#6B7D63] text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : (editingWaitlistEntry ? 'Update Entry' : 'Add to Waitlist')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
